import React, { useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { applyMapboxToken } from '../utils/mapboxToken';
import RescuePushTabs from '../components/rescuePush/RescuePushTabs';
import TargetPushPanel from '../components/rescuePush/TargetPushPanel';
import TrackPushPanel from '../components/rescuePush/TrackPushPanel';
import BindAndPickPanel from '../components/rescuePush/BindAndPickPanel';
import HistoryPanel from '../components/rescuePush/HistoryPanel';
import RescuePushMapPane from '../components/rescuePush/RescuePushMapPane';
import useRescuePushTarget from '../hooks/useRescuePushTarget';
import useRescuePushTrack from '../hooks/useRescuePushTrack';
import useRescuePushOverlays from '../hooks/useRescuePushOverlays';
import useRescuePushRecipients from '../hooks/useRescuePushRecipients';
import useRescuePushMap from '../hooks/useRescuePushMap';
import useRescuePushHistory from '../hooks/useRescuePushHistory';
import useRescuePushBind from '../hooks/useRescuePushBind';
import useRescuePushRealtime from '../hooks/useRescuePushRealtime';

applyMapboxToken(mapboxgl);

/**
 * 救援目标/轨迹推送页（薄壳：组合 hooks + 面板）
 */
function RescuePush() {
  const [activeTab, setActiveTab] = useState('target');

  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const rescueTargetMarkerRef = useRef(null);
  const trackPreviewMarkersRef = useRef([]);
  const trackLinesRef = useRef([]);
  const rescueTargetMarkersRef = useRef([]);

  // 跨 hook 桥接（避免初始化顺序 / 闭包过期问题）
  const loadPersistentDataRef = useRef(() => ({ targets: [], tracks: [] }));
  const loadFromHistoryRef = useRef(() => {});
  const savePersistentDataRef = useRef(() => {});
  const displayTrackOnMapRef = useRef(() => {});
  const displayTargetOnMapRef = useRef(() => {});
  const clearTrackPreviewRef = useRef(() => {});
  const showTrackPreviewRef = useRef(() => {});
  const clearAllTracksRef = useRef(() => {});
  const clearAllTargetsRef = useRef(() => {});
  const handleTrackReceivedRef = useRef(() => {});
  const handleTargetReceivedRef = useRef(() => {});
  const markTargetOnMapRef = useRef(() => {});
  const fetchPickPointTokensRef = useRef(() => {});
  const fetchTrackerTokensRef = useRef(() => {});

  const target = useRescuePushTarget({
    mapRef,
    markTargetOnMapRef,
    displayTargetOnMapRef
  });

  const track = useRescuePushTrack({
    clearTrackPreviewRef,
    showTrackPreviewRef,
    displayTrackOnMapRef
  });

  const overlays = useRescuePushOverlays({
    mapRef,
    trackPreviewMarkersRef,
    trackLinesRef,
    rescueTargetMarkersRef,
    setTrackPreviewCoords: track.setTrackPreviewCoords,
    setTrackPreviewInfo: track.setTrackPreviewInfo,
    setTrackFile: track.setTrackFile
  });

  const recipients = useRescuePushRecipients({
    setTargetForm: target.setTargetForm,
    setTrackForm: track.setTrackForm
  });

  const map = useRescuePushMap({
    mapContainer,
    mapRef,
    markersRef,
    rescueTargetMarkerRef,
    members: recipients.members,
    setTargetForm: target.setTargetForm,
    loadPersistentDataRef,
    displayTargetOnMapRef,
    displayTrackOnMapRef,
    loadFromHistoryRef,
    savePersistentDataRef
  });

  const history = useRescuePushHistory({
    activeTab,
    mapRef,
    rescueTargetMarkersRef,
    trackLinesRef,
    setPersistentTargets: overlays.setPersistentTargets,
    setPersistentTracks: overlays.setPersistentTracks
  });

  const bind = useRescuePushBind();

  useRescuePushRealtime({
    setTargetForm: target.setTargetForm,
    pickPointTokens: bind.pickPointTokens,
    setPickPointTokens: bind.setPickPointTokens,
    trackerTokens: bind.trackerTokens,
    handleTrackReceivedRef,
    handleTargetReceivedRef,
    clearAllTracksRef,
    clearAllTargetsRef,
    fetchPickPointTokensRef,
    fetchTrackerTokensRef
  });

  // 渲染期同步桥接（先于 map load / WS 回调，避免 effect 时序导致 undefined）
  loadPersistentDataRef.current = overlays.loadPersistentData;
  loadFromHistoryRef.current = overlays.loadFromHistory;
  savePersistentDataRef.current = overlays.savePersistentData;
  displayTrackOnMapRef.current = overlays.displayTrackOnMap;
  displayTargetOnMapRef.current = overlays.displayTargetOnMap;
  clearTrackPreviewRef.current = overlays.clearTrackPreview;
  showTrackPreviewRef.current = overlays.showTrackPreview;
  clearAllTracksRef.current = overlays.clearAllTracks;
  clearAllTargetsRef.current = overlays.clearAllTargets;
  handleTrackReceivedRef.current = overlays.handleTrackReceived;
  handleTargetReceivedRef.current = overlays.handleTargetReceived;
  markTargetOnMapRef.current = map.markTargetOnMap;
  fetchPickPointTokensRef.current = bind.fetchPickPointTokens;
  fetchTrackerTokensRef.current = bind.fetchTrackerTokens;

  return (
    <div
      style={{
        padding: 24,
        maxWidth: '100%',
        margin: '0 auto',
        minHeight: 'calc(100vh - var(--header-total-height))',
        boxSizing: 'border-box'
      }}
    >
      <h2 style={{ color: '#1976d2', marginBottom: 20 }}>救援目标/轨迹推送</h2>
      <RescuePushTabs activeTab={activeTab} setActiveTab={setActiveTab} />

      <div
        style={{
          display: 'flex',
          gap: 24,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          marginTop: -20
        }}
      >
        <div
          style={{
            flex: '0 0 500px',
            maxWidth: 520,
            minWidth: 460,
            overflow: 'auto'
          }}
        >
          {activeTab === 'target' && (
            <TargetPushPanel
              organizations={recipients.organizations}
              groups={recipients.groups}
              users={recipients.users}
              missions={recipients.missions || []}
              targetForm={target.targetForm}
              setTargetForm={target.setTargetForm}
              handleMultiSelect={recipients.handleMultiSelect}
              selectAll={recipients.selectAll}
              clearSelection={recipients.clearSelection}
              filterGroupsByOrg={recipients.filterGroupsByOrg}
              filterUsersByGroup={recipients.filterUsersByGroup}
              handleFormChange={target.handleFormChange}
              handleCoordinatePaste={target.handleCoordinatePaste}
              handleCoordinateParse={target.handleCoordinateParse}
              handleMapLinkChange={target.handleMapLinkChange}
              handleMapLinkParse={target.handleMapLinkParse}
              mapLinkParsing={target.mapLinkParsing}
              handleTargetPush={target.handleTargetPush}
              targetLoading={target.targetLoading}
              clearAllTargets={overlays.clearAllTargets}
              targetResult={target.targetResult}
            />
          )}

          {activeTab === 'bind' && (
            <BindAndPickPanel
              appCommandForm={bind.appCommandForm}
              setAppCommandForm={bind.setAppCommandForm}
              sendAppCommand={bind.sendAppCommand}
              appCommandLoading={bind.appCommandLoading}
              toggleAppCommandRecipient={bind.toggleAppCommandRecipient}
              trackerTokens={bind.trackerTokens}
              trackerForm={bind.trackerForm}
              setTrackerForm={bind.setTrackerForm}
              createTrackerToken={bind.createTrackerToken}
              assignStreamKey={bind.assignStreamKey}
              trackerLoading={bind.trackerLoading}
              handleTrackerTtlDaysChange={bind.handleTrackerTtlDaysChange}
              copyTrackerLink={bind.copyTrackerLink}
              updateTrackerMode={bind.updateTrackerMode}
              revokeTrackerToken={bind.revokeTrackerToken}
              pickPointForm={bind.pickPointForm}
              setPickPointForm={bind.setPickPointForm}
              createPickPointToken={bind.createPickPointToken}
              pickPointLoading={bind.pickPointLoading}
              pickPointTokens={bind.pickPointTokens}
              copyPickPointLink={bind.copyPickPointLink}
              revokePickPointToken={bind.revokePickPointToken}
              confirmPickPointToken={bind.confirmPickPointToken}
              deletePickPointToken={bind.deletePickPointToken}
              fetchPickPointTokens={bind.fetchPickPointTokens}
            />
          )}

          {activeTab === 'track' && (
            <TrackPushPanel
              organizations={recipients.organizations}
              groups={recipients.groups}
              users={recipients.users}
              trackForm={track.trackForm}
              setTrackForm={track.setTrackForm}
              handleMultiSelect={recipients.handleMultiSelect}
              selectAll={recipients.selectAll}
              clearSelection={recipients.clearSelection}
              filterGroupsByOrg={recipients.filterGroupsByOrg}
              filterUsersByGroup={recipients.filterUsersByGroup}
              handleFormChange={target.handleFormChange}
              handleTrackFileChange={track.handleTrackFileChange}
              trackPreviewLoading={track.trackPreviewLoading}
              trackPreviewInfo={track.trackPreviewInfo}
              trackPreviewCoords={track.trackPreviewCoords}
              handleTrackPush={track.handleTrackPush}
              trackLoading={track.trackLoading}
              clearAllTracks={overlays.clearAllTracks}
              trackResult={track.trackResult}
            />
          )}

          {activeTab === 'history' && (
            <HistoryPanel
              historyTab={history.historyTab}
              setHistoryTab={history.setHistoryTab}
              historyLoading={history.historyLoading}
              targetHistory={history.targetHistory}
              trackHistory={history.trackHistory}
              handleHistoryItemClick={history.handleHistoryItemClick}
              canDeleteHistoryItem={history.canDeleteHistoryItem}
              handleDelete={history.handleDelete}
              handleToggleFinished={history.handleToggleFinished}
            />
          )}
        </div>

        <RescuePushMapPane
          mapContainerRef={mapContainer}
          mapType={map.mapType}
          switchMapStyle={map.switchMapStyle}
          flyToCurrentLocation={map.flyToCurrentLocation}
        />
      </div>
    </div>
  );
}

export default RescuePush;
